/**
 * @fileoverview Create event page component.
 * @summary Page for organizers to create new events with venue selection.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/CreateEventPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch, useAppSelector } from '../../hooks/redux.js'
import { createEvent } from '../../store/slices/eventsSlice.js'
import { fetchVenues } from '../../store/slices/venuesSlice.js'
import Spinner from '../../components/Spinner.js'
import PageHeader from '../../components/PageHeader.js'
import { CreateEventForm } from './components/CreateEventForm.js'
import { NoVenuesWarning } from './components/NoVenuesWarning.js'
import QuotaExceededCreateModal from './components/QuotaExceededCreateModal.js'
import { createCheckoutSessionForEvent } from '../../services/api.js'
import { getErrorMessage } from '../../utils/errorUtils.js'
import { localToUtcISOString } from '../../utils/date.js'

/**
 * @summary Create event page component.
 * @returns {React.ReactElement} Create event page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function CreateEventPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { venues, isLoading: isLoadingVenues } = useAppSelector((state) => state.venues)
  const { isLoading: isCreating } = useAppSelector((state) => state.events)

  // Get venueId from query params if present
  const venueIdFromParams = searchParams.get('venueId') || ''

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    venueId: venueIdFromParams,
    date: '',
    time: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showQuotaModal, setShowQuotaModal] = useState(false)
  const [quotaErrorData, setQuotaErrorData] = useState<{ currentCount?: number; limit?: number; userPlan?: string } | null>(null)
  const [isCreatingOneShot, setIsCreatingOneShot] = useState(false)

  // Redirect if not authenticated or not organizer
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    if (user.role !== 'organizer' && user.role !== 'superadmin') {
      navigate('/', { replace: true })
      return
    }
  }, [user, navigate])

  // Fetch venues on mount
  useEffect(() => {
    if (user && (user.role === 'organizer' || user.role === 'superadmin')) {
      dispatch(fetchVenues())
    }
  }, [dispatch, user])

  // Update venueId when query params change
  useEffect(() => {
    const venueIdFromParams = searchParams.get('venueId') || ''
    if (venueIdFromParams && venueIdFromParams !== formData.venueId) {
      setFormData((prev) => ({ ...prev, venueId: venueIdFromParams }))
    }
  }, [searchParams, formData.venueId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    try {
      // Convert local date/time to UTC ISO string for backend
      const isoDateString = localToUtcISOString(formData.date, formData.time)

      const eventData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        venueId: formData.venueId || undefined,
        date: isoDateString,
        status: 'draft' as const,
      }

      await dispatch(createEvent(eventData)).unwrap()
      setSuccess(t('events.createSuccess') || 'Event created successfully')
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 1500)
    } catch (err: any) {
      // Extract error from rejectWithValue payload
      const error = err.payload || err
      
      // Check if error is quota exceeded for event creation
      if (error.code === 'QUOTA_EXCEEDED_CREATE' || error.message?.includes('quota') || error.message?.includes('limit')) {
        setQuotaErrorData({
          currentCount: error.data?.currentCount,
          limit: error.data?.limit,
          userPlan: error.data?.userPlan,
        })
        setShowQuotaModal(true)
      } else {
        // Translate all other errors using getErrorMessage
        const errorMessage = getErrorMessage(error, t)
        setError(errorMessage)
      }
    }
  }

  /**
   * @summary Create ONE_SHOT event via Stripe checkout
   * @description Creates checkout session with event data. Event will be created in webhook after payment.
   */
  const handleCreateOneShot = async () => {
    setIsCreatingOneShot(true)
    setError(null)

    try {
      // Convert local date/time to UTC ISO string for backend
      const isoDateString = localToUtcISOString(formData.date, formData.time)

      // Create checkout session with event data (event will be created after payment in webhook)
      const eventData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        venueId: formData.venueId || undefined,
        date: isoDateString,
      }

      const response = await createCheckoutSessionForEvent(eventData)

      if (!response.success || !response.data?.url) {
        throw new Error(response.message || t('subscription.upgradeError') || 'Failed to create checkout session')
      }

      // Redirect to Stripe checkout
      window.location.href = response.data.url
    } catch (err: any) {
      // Extract error from response
      const error = err.response?.data || err
      const errorMessage = getErrorMessage(error, t)
      setError(errorMessage)
      setIsCreatingOneShot(false)
      setShowQuotaModal(false)
    }
  }

  if (isLoadingVenues) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="w-full page-container">
        {/* Header */}
        <PageHeader
          title={t('events.createTitle') || 'Create Event'}
          subtitle={t('events.createSubtitle') || 'Create a new karaoke event'}
        />

      {/* Success/Error messages */}
      {success && (
        <div className="alert alert-success mb-6 flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="alert alert-error mb-6 flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}

      {/* Warning if no venues */}
      {venues.length === 0 ? (
        <NoVenuesWarning onCreateVenue={() => navigate('/venues')} />
      ) : (
        /* Create Event Form - Only shown if venues are available */
        <CreateEventForm
          formData={formData}
          isCreating={isCreating}
          venues={venues}
          onFormDataChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/dashboard')}
          onDateQuotaError={(error) => {
            setQuotaErrorData({
              currentCount: error.currentCount,
              limit: error.limit,
            })
            setShowQuotaModal(true)
          }}
        />
      )}

      {/* Quota Exceeded Modal */}
      {quotaErrorData && (
        <QuotaExceededCreateModal
          isOpen={showQuotaModal}
          isLoading={isCreatingOneShot}
          currentCount={quotaErrorData.currentCount}
          limit={quotaErrorData.limit}
          userPlan={quotaErrorData.userPlan}
          onCreateOneShot={handleCreateOneShot}
          onCancel={() => {
            setShowQuotaModal(false)
            setQuotaErrorData(null)
          }}
        />
      )}
      </div>
    </div>
  )
}

